// @ts-check
/**
 * Přepínač vzhledu: schéma (paleta) × režim (R-4 analýzy 1B).
 *
 * Kořen: `<form method="post" action="/appearance" data-module="appearance">` uvnitř popoveru
 * (`<div popover id="appearance-popover">`, otevírá `<button popovertarget>` — Popover API, bez JS).
 * Formulář má radio pole `name="palette"` (les | indigo | grafit) a `name="mode"`
 * (light | dark | system) a tlačítko „Použít" s třídou `js-hidden` (viditelné jen bez JS).
 *
 * Bez JS: odeslání `POST /appearance`, server zapíše cookie a vykreslí atributy (B2).
 * S JS: na `change` modul okamžitě nastaví `data-palette` / `data-mode` na `<html>`
 * (`system` = atribut odebrat, barvy pak řídí `prefers-color-scheme` v tokens.css), zapíše cookie
 * `etendry-appearance=<paleta>.<režim>` (rok, `Path=/`, `SameSite=Lax`, na https `Secure`), promítne
 * `data-mode` i na obaly teček schémat `[data-palette]` uvnitř formuláře (V1a sekce 3) a potlačí
 * odeslání formuláře. Žádné texty v JS; modul nečte žádné `data-*` kromě `data-module`.
 *
 * Fokus po otevření (ui-checklist bod 5, N1; sdílené `lib/popover.js` od sekce 7 R-11): na událost
 * `toggle` nejbližšího předka `[popover]` (`newState === 'open'`) modul fokusuje první zaškrtnuté
 * rádio v DOM = první skupina (od V1a
 * sekce 3 Režim, rádia jsou `sr-only` uvnitř `label` se stavem `has-checked:`), jinak první
 * pole formuláře. Ne přes `data-autofocus` — zavaděč `app.js` by ho fokusoval už při načtení
 * stránky (popover je zavřený). Popover je nemodální (`popover="auto"`): Esc, klik mimo a návrat
 * fokusu na spouštěč dává prohlížeč, past fokusu se nestaví. Bez Popover API (jsdom, starý
 * prohlížeč) událost nepřijde a modul jen nic nedělá — formulář dál funguje jako POST.
 */

import { focusOnOpen } from './lib/popover.js';

export const COOKIE_NAME = 'etendry-appearance';
const VALUE = /^[a-z]+$/;
const YEAR = 31536000;

/**
 * Hodnota zaškrtnutého radio pole, nebo `undefined`, není-li platná.
 * @param {HTMLFormElement} form
 * @param {string} name
 */
function checked(form, name) {
  const input = form.querySelector(`input[name="${name}"]:checked`);
  const value = input instanceof HTMLInputElement ? input.value : '';
  return VALUE.test(value) ? value : undefined;
}

/**
 * Promítne volbu do dokumentu a cookie.
 * @param {HTMLFormElement} form
 */
function apply(form) {
  const doc = form.ownerDocument;
  const html = doc.documentElement;
  const palette = checked(form, 'palette');
  const mode = checked(form, 'mode');
  if (palette) html.setAttribute('data-palette', palette);
  if (mode === 'system') html.removeAttribute('data-mode');
  else if (mode) html.setAttribute('data-mode', mode);
  // Tečky schémat v kartách popoveru (obal [data-palette] uvnitř formuláře) nesou týž data-mode
  // jako <html> — server ho vykreslil z cookie, po živé změně režimu by zůstaly ve starém.
  const current = html.getAttribute('data-mode');
  for (const dots of form.querySelectorAll('[data-palette]')) {
    if (current === null) dots.removeAttribute('data-mode');
    else dots.setAttribute('data-mode', current);
  }

  const p = palette ?? html.getAttribute('data-palette') ?? 'les';
  const m = mode ?? html.getAttribute('data-mode') ?? 'light';
  const secure = doc.defaultView?.location.protocol === 'https:' ? '; Secure' : '';
  doc.cookie = `${COOKIE_NAME}=${p}.${m}; Path=/; Max-Age=${YEAR}; SameSite=Lax${secure}`;
}

/**
 * Inicializace modulu nad kořenem `[data-module="appearance"]`.
 * @param {HTMLElement} root
 */
export default function init(root) {
  if (!(root instanceof HTMLFormElement)) return;
  root.addEventListener('change', () => apply(root));
  root.addEventListener('submit', (event) => {
    event.preventDefault();
    apply(root);
  });
  // Jediný dotek mimo kořen: `toggle` vzniká na popoveru, který formulář obaluje (lib/popover.js):
  // zaškrtnuté rádio první skupiny, jinak první pole formuláře.
  focusOnOpen(root, [
    'input[type="radio"]:checked',
    'input:not([type="hidden"]), select, textarea',
  ]);
}
